import { enumCompareStatus } from "./models/BaseObject";
import { State } from "./State";
import TreeNodeInfo from "./models/TreeNodeInfo";
import { copyAllDefinedProperties, loadProperties } from "./models/utils";
import BaseModelMap from "./models/BaseModelMap";
import BaseModel from "./models/BaseModel";
import { showDebugErrToast } from "./components/Toasts";
import Logger from "./Logger";

const logger = new Logger();

/**
 * Computes whether the given project states are identical.
 * This computation has side-effect: it mutates the compareStatus on
 * the objects used to display the change and manages a list of items
 * that are new in the other project within a list of the corresponding parent
 * on the current projects parent.
 * @param current state of the current project
 * @param other state of the other project
 * @returns whether the projects are equal
 */
export default function compareProjects(current: State, other: State): boolean {
  if (!current || !other) {
    return false;
  }

  const allEqual = [
    current.pageGroups.compareWithOtherMap(other.pageGroups, other),
    current.floors.compareWithOtherMap(other.floors, other),
    current.cabinets.compareWithOtherMap(other.cabinets, other),
    current.templateGroups.compareWithOtherMap(other.templateGroups, other),
    current.sceneGroup.compareWithOtherMap(other.sceneGroup, other),
    current.addresses.compareWithOtherMap(other.addresses, other),
    current.cableTypes.compareWithOtherMap(other.cableTypes, other),
  ].every((b) => b);

  state.objectRegistry.allObjects.forEach((obj) => {
    if (obj.compareStatus != enumCompareStatus.equal) {
      if (obj.parent) {
        obj.parent.dirtyChilds = true;
      }
    }
  });

  return allEqual;
}

export async function applyChanges(node: TreeNodeInfo) {
  // Case 1: Object missing in other Project
  if (node.obj.compareStatus & (enumCompareStatus.newInCurrentProject | enumCompareStatus.deletedInOtherProject)) {
    node.obj.removeFromObjectRegistry;
    // whether new in current or deleted in other, we want to remove this to apply the change in both cases
    const currentObj = state.objectRegistry.getObject(node.obj.id);
    currentObj?.delete();
    node.obj.compareStatus = enumCompareStatus.equal;
  }

  // Case 2: Object missing in this Project
  else if (node.obj.compareStatus & (enumCompareStatus.newInOtherProject | enumCompareStatus.deletedInCurrentProject)) {
    const parent = state.objectRegistry.getObject(node.parentTreeNodeInfo?.obj.id) as BaseModel;

    if (compareState) {
      let saveState = state;
      state = compareState;
      await state.menubarActions.onCopy(node.obj);
      state = saveState;

      const res = await state.menubarActions.onPaste2(parent, true);
      const newItems = res?.newItems;

      if (newItems) {
        for (const item of newItems) {
          item.compareStatus = enumCompareStatus.equal;

          const otherItem = compareState.objectRegistry.getObject(item.id) as BaseModel;
          if (otherItem) {
            const parentCollectionPropName = otherItem.getParentCollectionName();
            const parentCollection: BaseModelMap<BaseModel> = parentCollectionPropName
              ? (parent as any)?.[parentCollectionPropName]
              : undefined;
            if (parentCollection) {
              parentCollection.newInOtherProjectItems?.delete(node.obj.id);
            } else {
              logger.error("no parentCollection");
            }
          }
        }
      }
      parent.raiseOnChange("childCollection");
    }

    node.obj.compareStatus = enumCompareStatus.equal;
  }
  // Case 3: Object existed in Both trees and was modified
  else if (node.obj.compareStatus & enumCompareStatus.modified) {
    const currentObj = state.objectRegistry.getObject(node.obj.id) as BaseModel;
    const otherObj = globalThis.compareState?.objectRegistry.getObject(node.obj.id) as BaseModel;

    const parent = state.objectRegistry.getObject(node.parentTreeNodeInfo?.obj.id) as BaseModel;

    if (compareState) {
      let saveState = state;
      state = compareState;
      await state.menubarActions.onCopy(otherObj);
      state = saveState;

      let parentCollection: BaseModelMap<BaseModel> | undefined = undefined;
      if (parent.className == "BaseModelMap") {
        parentCollection = parent as BaseModelMap<BaseModel>;
      } else {
        const parentCollectionPropName = currentObj.getParentCollectionName();
        parentCollection = parentCollectionPropName ? (parent as any)?.[parentCollectionPropName] : undefined;
      }
      if (parentCollection) {
        parentCollection.remove(currentObj);
      }

      await state.menubarActions.onPaste2(parent, true);
    }

    parent.raiseOnChange("childCollection");
  }

  // Hopefully impossible else
  else {
    showDebugErrToast("Unhandled situation in ApplyChange encountered");
  }

  delete node.obj._state;
}
